/**
 * File Management Service
 * Feature: 006-file-management-ui
 * 
 * API service for file management operations
 */

import apiClient from '../core/axios';
import type APIResponse from '../types/response.type';
import type {
  FileListResponse,
  FileListParams,
  FileDetailResponse,
  FileResponse,
  FileResponseWithDependencies,
  CreateDependencyRequest,
  PreviewResponse,
  DownloadUrlResponse,
  UploadFileOptions,
  UpdateFileDto,
  BulkDeleteRequest,
  BulkDeleteResponse,
  ReplaceFileOptions,
  ReplaceResponse,
  VersionHistoryResponse,
  StorageUsageResponse,
  StorageBreakdownResponse,
  InitiateUploadRequest,
  InitiateUploadResponse,
  ConfirmUploadRequest,
  ConfirmUploadResponse
} from '../types/files';

class FilesService {
  /**
   * List files with pagination and filters
   */
  async listFiles(params: FileListParams = {}): Promise<APIResponse<FileListResponse>> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.category) queryParams.append('category', params.category);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.includeDependencies) queryParams.append('includeDependencies', 'true');
    
    const response = await apiClient.get(`/files?${queryParams.toString()}`);
    return response.data;
  }

  /**
   * Get detailed information about a specific file
   */
  async getFile(id: string): Promise<APIResponse<FileDetailResponse>> {
    const response = await apiClient.get(`/files/${id}`);
    return response.data;
  }

  /**
   * Get file preview data (image, video, audio, PDF, or spreadsheet)
   */
  async getPreview(id: string): Promise<APIResponse<PreviewResponse>> {
    const response = await apiClient.get(`/files/${id}/preview`);
    return response.data;
  }

  /**
   * Get a signed download URL for a file
   */
  async getDownloadUrl(id: string, expiresIn = 3600): Promise<APIResponse<DownloadUrlResponse>> {
    const response = await apiClient.get(`/files/${id}/download?expiresIn=${expiresIn}`);
    return response.data;
  }

  /**
   * Step 1: Initiate upload - Get presigned URL for direct S3 upload
   */
  async initiateUpload(request: InitiateUploadRequest): Promise<APIResponse<InitiateUploadResponse>> {
    const response = await apiClient.post('/files', request, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  }

  /**
   * Step 2: Upload file directly to S3 using presigned URL
   * Note: This bypasses the backend and uploads directly to S3
   */
  async uploadToS3(
    uploadUrl: string,
    file: File,
    requiredHeaders: Record<string, string>,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else if (xhr.status === 0) {
          // Status 0 typically indicates CORS error or network failure
          reject(new Error('CORS_ERROR: Unable to upload directly to storage. The server may not allow cross-origin requests.'));
        } else {
          reject(new Error(`S3 upload failed with status ${xhr.status}: ${xhr.responseText}`));
        }
      });

      xhr.addEventListener('error', () => {
        // Network error or CORS failure - browser blocks the response
        reject(new Error('CORS_OR_NETWORK_ERROR: Failed to upload file to storage'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload cancelled'));
      });

      xhr.open('PUT', uploadUrl);
      
      // Set required headers from presigned URL response
      // Note: Some headers may trigger CORS preflight which can fail if bucket CORS is not configured
      Object.entries(requiredHeaders).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.send(file);
    });
  }

  /**
   * Step 3: Confirm upload - Notify backend that S3 upload is complete
   */
  async confirmUpload(request: ConfirmUploadRequest): Promise<APIResponse<ConfirmUploadResponse>> {
    const response = await apiClient.post('/files/confirm', request);
    return response.data;
  }

  /**
   * Complete upload flow: Initiate -> Upload to S3 -> Confirm
   * Uses direct-to-S3 upload for better performance
   */
  async uploadFile(
    file: File,
    options: UploadFileOptions = {},
    onProgress?: (percent: number, stage: 'initiating' | 'uploading' | 'confirming') => void
  ): Promise<APIResponse<ConfirmUploadResponse>> {
    // Step 1: Initiate upload (10% progress)
    onProgress?.(10, 'initiating');
    
    // Build request, only including optional fields if they have values
    const initRequest: InitiateUploadRequest = {
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      size: file.size,
    };
    
    // Only add category if provided (backend may reject undefined)
    if (options.category) {
      initRequest.category = options.category;
    }
    
    // Only add tags if provided and non-empty
    if (options.tags && options.tags.length > 0) {
      initRequest.tags = options.tags;
    }
    
    const initResponse = await this.initiateUpload(initRequest);

    // Handle different response structures - API may return data directly or nested
    const initData = (initResponse.data || initResponse) as InitiateUploadResponse;
    const { uploadId, uploadUrl, requiredHeaders } = initData;

    if (!uploadUrl || !uploadId) {
      throw new Error('Failed to get presigned URL from server');
    }

    // Step 2: Upload to S3 (10-80% progress)
    await this.uploadToS3(
      uploadUrl,
      file,
      requiredHeaders || {},
      (percent) => {
        // Map 0-100 to 10-80
        const adjustedProgress = 10 + Math.round(percent * 0.7);
        onProgress?.(adjustedProgress, 'uploading');
      }
    );

    // Step 3: Confirm upload (80-100% progress)
    onProgress?.(80, 'confirming');
    
    const confirmResponse = await this.confirmUpload({ uploadId });
    
    onProgress?.(100, 'confirming');

    return confirmResponse;
  }

  /**
   * Update file metadata (name, category, tags)
   */
  async updateFile(id: string, data: UpdateFileDto): Promise<APIResponse<FileResponse>> {
    const response = await apiClient.patch(`/files/${id}`, data);
    return response.data;
  }

  /**
   * Delete a single file (fails if file has dependencies)
   */
  async deleteFile(id: string): Promise<APIResponse<void>> {
    const response = await apiClient.delete(`/files/${id}`);
    return response.data;
  }

  /**
   * Bulk delete multiple files
   * Returns summary of deleted and skipped files
   */
  async bulkDelete(request: BulkDeleteRequest): Promise<APIResponse<BulkDeleteResponse>> {
    const response = await apiClient.post('/files/bulk/delete', request);
    return response.data;
  }

  /**
   * Replace file content while maintaining references
   * Uses direct-to-S3 upload flow
   */
  async replaceFile(
    id: string,
    file: File,
    options: ReplaceFileOptions = {},
    onProgress?: (percent: number, stage: 'initiating' | 'uploading' | 'confirming') => void
  ): Promise<APIResponse<ReplaceResponse>> {
    // Step 1: Initiate replace upload
    onProgress?.(10, 'initiating');
    
    // Build request, only including optional fields if they have values
    const replaceRequest: Record<string, any> = {
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      size: file.size,
    };
    
    if (options.name) {
      replaceRequest.name = options.name;
    }
    if (options.force !== undefined) {
      replaceRequest.force = options.force;
    }
    if (options.versionNote) {
      replaceRequest.versionNote = options.versionNote;
    }
    
    const initResponse = await apiClient.post(`/files/${id}/replace`, replaceRequest, {
      headers: { 'Content-Type': 'application/json' }
    });

    const initData = initResponse.data?.data || initResponse.data;
    const { uploadId, uploadUrl, requiredHeaders } = initData || {};

    if (!uploadUrl || !uploadId) {
      throw new Error('Failed to get presigned URL for file replacement');
    }

    // Step 2: Upload to S3
    await this.uploadToS3(
      uploadUrl,
      file,
      requiredHeaders || {},
      (percent) => {
        const adjustedProgress = 10 + Math.round(percent * 0.7);
        onProgress?.(adjustedProgress, 'uploading');
      }
    );

    // Step 3: Confirm replace upload
    onProgress?.(80, 'confirming');
    
    const confirmResponse = await apiClient.post(`/files/${id}/replace/confirm`, { uploadId });
    
    onProgress?.(100, 'confirming');

    return confirmResponse.data;
  }

  /**
   * Get version history for a file
   */
  async getVersions(id: string): Promise<APIResponse<VersionHistoryResponse>> {
    const response = await apiClient.get(`/files/${id}/versions`);
    return response.data;
  }

  /**
   * Get organization storage usage
   */
  async getStorageUsage(): Promise<APIResponse<StorageUsageResponse>> {
    const response = await apiClient.get('/storage');
    return response.data;
  }

  /**
   * Get storage breakdown by category with largest files
   */
  async getStorageBreakdown(): Promise<APIResponse<StorageBreakdownResponse>> {
    const response = await apiClient.get('/storage/breakdown');
    return response.data;
  }

  /**
   * Create dependency between file and entity
   * Feature: 009-unified-file-upload
   */
  async createDependency(request: {
    fileId: string;
    entityType: string;
    entityId: string;
    entityName: string;
    fieldName: string;
  }): Promise<APIResponse<void>> {
    const response = await apiClient.post(`/files/${request.fileId}/dependencies`, {
      entityType: request.entityType,
      entityId: request.entityId,
      entityName: request.entityName,
      fieldName: request.fieldName,
    });
    return response.data;
  }

  /**
   * Remove dependency between file and entity
   * Feature: 009-unified-file-upload
   */
  async removeDependency(
    fileId: string,
    entityType: string,
    entityId: string
  ): Promise<APIResponse<void>> {
    const response = await apiClient.delete(
      `/files/${fileId}/dependencies?entityType=${entityType}&entityId=${entityId}`
    );
    return response.data;
  }

  /**
   * Get file details with dependencies
   * Feature: 009-unified-file-upload
   */
  async getFileWithDependencies(id: string): Promise<APIResponse<FileDetailResponse>> {
    const response = await apiClient.get(`/files/${id}?includeDependencies=true`);
    return response.data;
  }
}

export default new FilesService();
